import { useState, useEffect, useCallback } from "react";
import supabase from "@/lib/supabase";

const ACTIVE_COHORT_KEY = "active_cohort_id";

export interface Cohort {
  id: number;
  name: string;
  created_at: string;
}

export default function useCohortSettings() {
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [activeCohortId, setActiveCohortId] = useState<number | null>(() => {
    try {
      const raw = localStorage.getItem(ACTIVE_COHORT_KEY);
      return raw ? Number(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCohorts = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from("cohorts")
        .select("*")
        .order("created_at", { ascending: true });

      if (fetchError) throw fetchError;

      const list = data as Cohort[];
      setCohorts(list);

      // auto-select first cohort if none is active
      if (!activeCohortId && list.length > 0) {
        const first = list[0].id;
        setActiveCohortId(first);
        try {
          localStorage.setItem(ACTIVE_COHORT_KEY, String(first));
        } catch {
          // storage unavailable
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "기수 정보를 불러오지 못했습니다");
    } finally {
      setLoading(false);
    }
  }, [activeCohortId]);

  useEffect(() => {
    fetchCohorts();
  }, []);

  const createCohort = useCallback(async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;

    const { data, error: insertError } = await supabase
      .from("cohorts")
      .insert({ name: trimmed })
      .select()
      .single();

    if (insertError) throw insertError;

    const created = data as Cohort;
    setCohorts((prev) => [...prev, created]);
    return created;
  }, []);

  const setActive = useCallback((id: number) => {
    setActiveCohortId(id);
    try {
      localStorage.setItem(ACTIVE_COHORT_KEY, String(id));
    } catch {
      // storage unavailable
    }
  }, []);

  const getActiveCohortId = useCallback((): number | null => {
    try {
      const raw = localStorage.getItem(ACTIVE_COHORT_KEY);
      return raw ? Number(raw) : null;
    } catch {
      return null;
    }
  }, []);

  return {
    cohorts,
    activeCohortId,
    loading,
    error,
    createCohort,
    setActive,
    getActiveCohortId,
    refetch: fetchCohorts,
  };
}